// Import package components
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Image, Flex, Select, SelectOption } from "uikit-react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import moment from "moment";
import classnames from "classnames";

import { Link } from "../../ui";

import { AppContexts } from "../../providers";

// Import common components
import RecordsPerPage from "../common/RecordsPerPage";
import Pagination from "../common/Pagination";

import MacroAddImportField from "./add_import_field";
import MacroAddCustomField from "./add_custom_field";
import UIkit from "uikit";

const categoryMap = ["Import Field", "Custom Field"];

const Macros = () => {
  const { get, settings } = useContext(AppContexts.SettingsContext);
  const { pmsFields, getPmsFields, loading } = useContext(
    AppContexts.UtilContext,
  );
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;
  const [criteria, setCriteria] = useState({
    search_term: "",
    type: null,
    page: 0,
  });
  const [selectedMacro, setSelectedMacro] = useState(null);

  const getMacroName = useCallback(
    (macro) => {
      if (macro?.category === "Unsynced") {
        return macro.field;
      }
      if (!pmsFields || !macro) {
        return "";
      }

      if (!!pmsFields[macro.category]) {
        return pmsFields[macro.category][macro.field];
      } else {
        return "Macro No Longer Exists";
      }
    },
    [pmsFields, settings.custom_macros],
  );

  useEffect(() => {
    document.title = "Macros - RueBaRue";
    get(["custom_macros"]);
    if (!pmsFields) {
      getPmsFields();
    }
  }, []);

  const filteredMacros = useMemo(() => {
    return (
      settings.custom_macros?.filter((macro) => {
        if (criteria.search_term) {
          return (
            macro.field
              .toLowerCase()
              .includes(criteria.search_term.toLowerCase()) ||
            macro.macro.toLowerCase().includes(criteria.search_term.toLowerCase())
          );
        }
        if (criteria.type) {
          if (criteria.type === "Import") {
            return macro.category !== "Unsynced";
          } else {
            return macro.category === "Unsynced";
          }
        }

        return true;
      }) || []
    );
  }, [settings.custom_macros, criteria]);

  const pageShowMacros = useMemo(() => {
    return (
      filteredMacros.slice(
        recordsPerPage * criteria.page,
        recordsPerPage * (criteria.page + 1),
      ) || []
    );
  }, [filteredMacros, criteria.page, recordsPerPage]);

  const handleEdit = useCallback((macro) => {
    setSelectedMacro(macro);
  });

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((criteria) => {
      const next = { ...criteria, [key]: value };
      // if user changes the search term or type filter, reset to first page
      if (key === "search_term" || key === "type") {
        next.page = 0;
      }
      return next;
    });
  });

  const handleIndexChanged = useCallback((newPageIndex) => {
    handleCriteriaChange("page", newPageIndex);
  });

  return (
    <>
      <section id="schedule-a-message">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Macros</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #add-import-field-modal"
                onClick={() => setSelectedMacro(null)}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Import Field
              </button>
              <button
                className="uk-button hms-btn hms-white-btn"
                uk-toggle="target: #add-custom-field-modal"
                onClick={() => setSelectedMacro(null)}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Custom Field
              </button>
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form
                    action=""
                    className="uk-form uk-flex uk-flex-middle hms-form"
                  >
                    <div className="uk-inline">
          <div className="uk-search uk-search-default uk-form-custom uk-margin-small-right">
            {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name"
                          value={criteria.search_term}
                          onChange={(event) =>
                            handleCriteriaChange(
                              "search_term",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                      <div uk-form-custom="target: > * > span:first-child">
                        <Select
                          value={criteria.type === null ? "All" : criteria.type}
                          onChange={(e) =>
                            handleCriteriaChange(
                              "type",
                              e.target.value === "All" ? null : e.target.value,
                            )
                          }
                        >
                          <SelectOption value="All">All</SelectOption>
                          {categoryMap.map((type, index) => (
                            <SelectOption value={type} key={index}>
                              {type}
                            </SelectOption>
                          ))}
                        </Select>
                        <button
                          className="uk-button uk-button-default"
                          type="button"
                          tabIndex="-1"
                        >
                          <span></span>
                          <span uk-icon="icon: chevron-down"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </Flex>
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Field</th>
                    <th className="uk-table-expand">Macro</th>
                    <th className="uk-table-expand">Type</th>
                    <th className="actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {pageShowMacros.map((macro) => (
                    <tr key={`${macro.category}-${macro.field}-${macro.macro}`}>
                      <td className="semi-bold-font">{getMacroName(macro)}</td>
                      <td>{macro.macro}</td>
                      <td>
                        {macro.category === "Unsynced"
                          ? "Custom Field"
                          : "Import Field"}
                      </td>
                      <td className="hms-action-btn">
                        <div className="uk-flex uk-flex-top">
                          <div className="uk-inline">
                            <button
                              className="uk-button"
                              uk-toggle={`target: ${macro.category === "Unsynced" ? "#add-custom-field-modal" : "#add-import-field-modal"}`}
                              onClick={() => handleEdit(macro)}
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={filteredMacros.length}
              index={criteria.page}
              onIndexChanged={handleIndexChanged}
            />
            {filteredMacros.length != 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>
      <MacroAddImportField macro={selectedMacro} />
      <MacroAddCustomField macro={selectedMacro} />
    </>
  );
};

export default Macros;
